import { useState, useEffect, useCallback } from 'react';
import { MarketplaceItem, MarketplaceFilters, MarketplaceSearchParams, MarketplaceCategory } from '../types/marketplace';
import { exampleDataItem13, exampleDataItem14, exampleDataItem15, exampleDataItem16, exampleDataItem17, exampleDataItem18, exampleDataItem19, exampleDataItem20, exampleMarketPlaceItem1, exampleMarketPlaceItem10, exampleMarketPlaceItem11, exampleMarketPlaceItem12, exampleMarketPlaceItem13, exampleMarketPlaceItem2, exampleMarketPlaceItem3, exampleMarketPlaceItem4, exampleMarketPlaceItem5, exampleMarketPlaceItem6, exampleMarketPlaceItem7, exampleMarketPlaceItem8, exampleMarketPlaceItem9 } from '../components/marketplace/exampleMarketPlaceData';

const exampleItems: MarketplaceItem[] = [
  exampleMarketPlaceItem1,
  exampleMarketPlaceItem2,
  exampleMarketPlaceItem3,
  exampleMarketPlaceItem4,
  exampleMarketPlaceItem5,
  exampleMarketPlaceItem6,
  exampleMarketPlaceItem7,
  exampleMarketPlaceItem8,
  exampleMarketPlaceItem9,
  exampleMarketPlaceItem10,
  exampleMarketPlaceItem11,
  exampleMarketPlaceItem12,
  exampleMarketPlaceItem13,
  exampleDataItem13,
  exampleDataItem14,
  exampleDataItem15,
  exampleDataItem16,
  exampleDataItem17,
  exampleDataItem18,
  exampleDataItem19,
  exampleDataItem20,
];

interface UseMarketplaceReturn {
  items: MarketplaceItem[];
  trendingItems: MarketplaceItem[];
  featuredItems: MarketplaceItem[];
  categories: MarketplaceCategory[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  searchItems: (params: MarketplaceSearchParams) => Promise<void>;
  publishItem: (itemData: Partial<MarketplaceItem>) => Promise<MarketplaceItem>;
  downloadItem: (itemId: string) => Promise<void>;
  getItemDetails: (itemId: string) => Promise<MarketplaceItem>;
}

export const useMarketplace = (): UseMarketplaceReturn => {
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [trendingItems, setTrendingItems] = useState<MarketplaceItem[]>([]);
  const [featuredItems, setFeaturedItems] = useState<MarketplaceItem[]>([]);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const searchItems = useCallback(async (params: MarketplaceSearchParams) => {
    setLoading(true);
    setError(null);

    try {
      let filtered = [...exampleItems];

      if (params.query) {
        const q = params.query.toLowerCase();
        filtered = filtered.filter(item =>
          item.title.toLowerCase().includes(q) || item.description.toLowerCase().includes(q)
        );
      }
      if (params.category) {
        filtered = filtered.filter(item => item.category === params.category);
      }
      if (params.tags?.length) {
        filtered = filtered.filter(item => params.tags!.every(tag => item.tags.includes(tag)));
      }
      if (params.type) {
        filtered = filtered.filter(item => item.type === params.type);
      }
      if (params.minRating !== undefined) {
        filtered = filtered.filter(item => item.rating >= params.minRating!);
      }
      if (params.priceRange?.min !== undefined) {
        filtered = filtered.filter(item => item.price >= params.priceRange!.min);
      }
      if (params.priceRange?.max !== undefined && params.priceRange.max !== Infinity) {
        filtered = filtered.filter(item => item.price <= params.priceRange!.max);
      }

      if (params.sortBy === 'popularity') filtered.sort((a, b) => b.downloadCount - a.downloadCount);
      else if (params.sortBy === 'rating') filtered.sort((a, b) => b.rating - a.rating);
      else if (params.sortBy === 'newest') filtered.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      else if (params.sortBy === 'price_low') filtered.sort((a, b) => a.price - b.price);
      else if (params.sortBy === 'price_high') filtered.sort((a, b) => b.price - a.price);

      const page = params.pagination?.page || 1;
      const limit = params.pagination?.limit || 20;
      setItems(filtered.slice((page - 1) * limit, page * limit));
      setTotalPages(Math.ceil(filtered.length / limit));
      setCurrentPage(page);
    } catch (err) {
      console.error('Error searching marketplace:', err);
      setError(err instanceof Error ? err.message : 'Failed to search marketplace');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const publishItem = useCallback(async (itemData: Partial<MarketplaceItem>): Promise<MarketplaceItem> => {
    const newItem: MarketplaceItem = { ...itemData, id: String(exampleItems.length) } as MarketplaceItem;
    exampleItems.push(newItem);
    return newItem;
  }, []);

  const downloadItem = useCallback(async (itemId: string) => {
    const item = exampleItems.find(i => i.id === itemId);
    if (!item) throw new Error('Item not found');
  }, []);

  const getItemDetails = useCallback(async (itemId: string): Promise<MarketplaceItem> => {
    const item = exampleItems.find(i => i.id === itemId);
    if (!item) throw new Error('Item not found');
    return item;
  }, []);

  useEffect(() => {
    const trending = [...exampleItems].sort((a, b) => b.downloadCount - a.downloadCount).slice(0, 10);
    const featured = exampleItems.filter(item => item.isFeatured).slice(0, 6);
    const categoryNames = Array.from(new Set(exampleItems.map(i => i.category)));
    const categoryList: MarketplaceCategory[] = categoryNames.map((name, idx) => ({
      id: String(idx),
      name,
      description: '',
      icon: '',
      color: '',
    }));

    setTrendingItems(trending);
    setFeaturedItems(featured);
    setCategories(categoryList);
    searchItems({ sortBy: 'popularity', pagination: { page: 1, limit: 20 } });
  }, [searchItems]);

  return {
    items,
    trendingItems,
    featuredItems,
    categories,
    loading,
    error,
    totalPages,
    currentPage,
    searchItems,
    publishItem,
    downloadItem,
    getItemDetails,
  };
};
